/**
 * Admin console: the pure rules and copy shared by today's console (v1,
 * components/AdminConsole) and the redesigned one (v2, components/AdminV2),
 * so the two cannot drift. Nothing here talks to the API or holds state;
 * see hooks/useAdminConsole.js and friends for that.
 */

export const TYPE_LABEL = { VENDOR: 'Fusable', TOG: 'Holding', COG: 'Dealer' };
// The edit dialog also names a legacy store org (the backend no longer creates them).
export const EDIT_TYPE_LABEL = { ...TYPE_LABEL, LOCATION: 'Store' };
export const ROLE_LABEL = { super_admin: 'Super Admin', dealer_admin: 'Dealer Admin', user: 'User' };
export const ROLE_VALUES = ['user', 'dealer_admin', 'super_admin'];
export const ROLE_OPTIONS = ROLE_VALUES.map((value) => ({ value, label: ROLE_LABEL[value] }));
// The data a user sees = the level of their org membership (dealer is the deepest level).
export const PERSPECTIVE_LABEL = { VENDOR: 'All data', TOG: 'Holding view', COG: 'Dealership view' };
// The Add user hint: "This decides what they see: <…>".
export const PERSPECTIVE_HINT = {
    VENDOR: 'all data across the vendor organization',
    TOG: 'the whole holding',
    COG: 'the whole dealership',
};

// ── Copy (today's wording; the two fixes from DECISIONS.md are marked) ──
export const COPY = {
    loadError: 'Could not load admin data. Check your access.',
    updateFailed: 'Update failed.',
    removeFailed: 'Remove failed.',
    resetFailed: 'Password reset failed.',
    addFailed: 'Could not add.',
    deleteFailed: 'Delete failed.',
    addUserFailed: 'Could not add user.',
    saveOrgFailed: 'Could not save changes.',
    onboardFailed: 'Could not create the dealership.',
    tenantsLoadError: 'Could not load data sources. Check your access.',
    // Bug fix (DECISIONS.md): a failed directory search is an outage, not "no matches".
    directoryError: 'The directory could not be searched right now. Try again shortly.',
    // Bug fix (DECISIONS.md): the temporary password is emailed, never handed over.
    onboardAdminHint: 'A local account is created and a temporary password is emailed to them. You’ll see whether the email went out.',
    footnoteUsers: 'Users come from your Entra directory. “Reset PW” issues a temporary password the user must change at next sign-in.',
    slugWarning: (type) => `Changing the address breaks existing sign-in links for this ${type}.`,
    suspendWarning: 'Suspending freezes every sub-organization and user beneath — no one can sign in until reactivated.',
    deleteBody: (type) => `This removes the ${type} from IronSight. Its sub-organizations and users must be moved or removed first.`,
};

/** The sent / not-sent result of a reset, an invite or an onboarding invite. */
export const resultCopy = (sent) => (sent
    ? { title: 'Emailed to the user', before: 'We emailed the sign-in details to ', after: '. They set their own password on first sign-in.' }
    : {
        title: 'Email could not be sent',
        before: 'The account for ',
        after: ' exists, but the sign-in details could not be delivered — so they cannot sign in yet. Check the email service, then use Reset password to send them again.',
    });

// ── Tree helpers ───────────────────────────────────────────────────────
export function findNode(nodes, id) {
    for (const n of nodes || []) {
        if (n.id === id) return n;
        const hit = findNode(n.children, id);
        if (hit) return hit;
    }
    return null;
}

export function countAll(nodes, type) {
    let c = 0;
    for (const n of nodes || []) { if (n.type === type) c += 1; c += countAll(n.children, type); }
    return c;
}

/** Depth-first list of every org: { id, name, slug, type, status, depth, parentId, node }. */
export function flattenOrgs(nodes, depth = 0, out = [], parentId = null) {
    for (const n of nodes || []) {
        out.push({ id: n.id, name: n.name, slug: n.slug, type: n.type, status: n.status, depth, parentId, node: n });
        if (n.children?.length) flattenOrgs(n.children, depth + 1, out, n.id);
    }
    return out;
}

export function collectByType(nodes, type, out = []) {
    for (const n of nodes || []) { if (n.type === type) out.push(n); collectByType(n.children, type, out); }
    return out;
}

export function slugify(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function initials(u) {
    const s = (u.display_name || u.email || '?').trim();
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return s.slice(0, 2).toUpperCase();
}

export function tileFoot(node) {
    const kids = node.children?.length || 0;
    if (node.type === 'TOG') return `${kids} ${kids === 1 ? 'dealership' : 'dealerships'}`;
    if (node.type === 'VENDOR') return `${kids} ${kids === 1 ? 'organization' : 'organizations'}`;
    return 'Manage team'; // a dealer is the leaf — drill in for its team
}

/** Only the vendor organization grants admin roles; a dealer-admin adds plain users only. */
export const roleChoicesFor = (isSuper) => (isSuper ? ROLE_VALUES : ['user']);

export const isAdminRole = (role) => role === 'dealer_admin' || role === 'super_admin';

/** The KPI tiles of today's console for the level in view. */
export function kpisFor(currentNode, orgs, users) {
    const admins = users.filter((u) => isAdminRole(u.role)).length;
    if (!currentNode) {
        return [
            { label: 'Holdings', value: countAll(orgs, 'TOG') },
            { label: 'Dealerships', value: countAll(orgs, 'COG') },
            { label: 'Users', value: users.length },
        ];
    }
    if (currentNode.type === 'TOG') {
        return [
            { label: 'Dealerships', value: (currentNode.children || []).filter((c) => c.type === 'COG').length },
            { label: 'Team', value: users.length },
            { label: 'Admins', value: admins },
        ];
    }
    return [
        { label: 'Team members', value: users.length },
        { label: 'Admins', value: admins },
    ];
}

/** Client-side, case-insensitive substring on display name or email. */
export function searchUsers(users, query) {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter((u) => (u.display_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
}

/** The backend's `detail` text of a failed admin call, else the fallback. */
export const apiDetail = (e, fallback) => e?.details?.detail || fallback;

/** Same, for the modals that also fall back to the error's own message. */
export const apiDetailOrMessage = (e, fallback) => e?.details?.detail || e?.message || fallback;

/** Where an org may move: a dealer → standalone or under any holding. */
export function moveOptionsFor(org, orgs) {
    if (org.type !== 'COG') return [];
    return [
        { id: '', label: '— Standalone (top level)' },
        ...collectByType(orgs, 'TOG').map((h) => ({ id: h.id, label: `Holding · ${h.name}` })),
    ];
}

/** "Fusable staff" / "Holding user" / "Dealer user", from the home org's type. */
export const USER_KIND_LABEL = { staff: 'Fusable staff', holding: 'Holding user', dealer: 'Dealer user' };
export function userKind(user, orgs) {
    const type = findNode(orgs, user.org_id)?.type;
    if (type === 'VENDOR') return 'staff';
    if (type === 'TOG') return 'holding';
    return 'dealer';
}

/** Status pill text: today's raw status, capitalised. */
export const STATUS_LABEL = { active: 'Active', suspended: 'Suspended', pending: 'Pending' };
export const statusLabel = (s) => STATUS_LABEL[s] || s || '—';

/** The sign-in address of an org: always its own slug (login resolves Org.slug). */
export const signInPath = (org) => (org?.slug ? `/login/${org.slug}` : null);

/**
 * What blocks deleting an org, as the server would refuse it: sub-orgs,
 * users who are members of exactly that org, an attached data source.
 * `users` is the full (root) user list; `tenants` may be empty for callers
 * who cannot read them.
 */
export function deleteBlockers(org, users, tenants) {
    const children = org.children?.length || 0;
    const members = (users || []).filter((u) => u.org_id === org.id).length;
    const source = (tenants || []).some((t) => t.org_id === org.id);
    return { children, members, source, blocked: Boolean(children || members || source) };
}

/** The Delete pre-check sentence, or null when nothing blocks it. */
export function deleteBlockerText({ children, members, source }) {
    const parts = [];
    if (members) parts.push(`${members} ${members === 1 ? 'user' : 'users'}`);
    if (children) parts.push(`${children} ${children === 1 ? 'sub-organization' : 'sub-organizations'}`);
    const lines = [];
    if (parts.length) lines.push(`Move or remove its ${parts.join(' and ')} first.`);
    if (source) lines.push('Detach its data source first.');
    return lines.length ? lines.join(' ') : null;
}

/**
 * Guards the console shows before a call the server would refuse anyway.
 * Mirrors router.py: nobody demotes, deactivates or removes themselves;
 * a dealer-admin cannot manage admin accounts. (The last-super-admin rule is
 * deliberately left to the server: its count includes suspended accounts.)
 */
export function userGuard(user, action, { me, isSuper }) {
    const self = Boolean(me?.email) && user.email === me.email;
    if (!isSuper && isAdminRole(user.role) && action !== 'view') return 'Only Fusable can manage admin accounts';
    if (self && action === 'role') return 'You cannot change the role of your own account';
    if (self && action === 'status') return 'You cannot deactivate your own account';
    if (self && action === 'remove') return 'You cannot remove your own account';
    return null;
}

/** Tenants grouped for the Data sources view. */
export function groupTenants(tenants, orgs) {
    const dealers = flattenOrgs(orgs).filter((o) => o.type === 'COG');
    const dealerIds = new Set(dealers.map((d) => d.id));
    const attached = new Set(tenants.map((t) => t.org_id));
    return {
        dealers,
        // Tenants whose org is a dealership in the tree.
        assigned: tenants.filter((t) => dealerIds.has(t.org_id)),
        // Orphans: the org is gone, or is not a dealership (decision 9).
        unassigned: tenants.filter((t) => !dealerIds.has(t.org_id)),
        unattached: dealers.filter((d) => !attached.has(d.id)),
    };
}

/** Dealerships without an active source: the Data sources "to fix" count. */
export function sourcesToFix(tenants, orgs) {
    const byOrg = new Map(tenants.map((t) => [t.org_id, t]));
    return collectByType(orgs, 'COG').filter((d) => byOrg.get(d.id)?.status !== 'active').length;
}
